import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { RotateCcw, Loader2 } from "lucide-react";
import { usePageHeader } from "@/components/layouts/lib/page-header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { partyApi, type Party } from "@/lib/party.api";
import { proformaApi, type Proforma } from "@/lib/proforma.api";
import { invoiceApi, type Invoice } from "@/lib/invoice.api";
import { usersApi, type User } from "@/lib/users.api";
import { commissionApi, type CommissionTransaction } from "@/lib/commission.api";
import { stockApi, type StockArticle } from "@/lib/stock.api";
import { toast } from "sonner";

interface TrashModule<T> {
  key: string;
  label: string;
  list: () => Promise<{ data: T[] }>;
  restore: (id: number) => Promise<unknown>;
  getId: (item: T) => number;
  getLabel: (item: T) => string;
}

const modules: TrashModule<any>[] = [
  {
    key: "parties",
    label: "Parties",
    list: partyApi.trash,
    restore: partyApi.restore,
    getId: (p: Party) => p.id,
    getLabel: (p: Party) => p.fullName,
  },
  {
    key: "proformas",
    label: "Proformas",
    list: proformaApi.trash,
    restore: proformaApi.restore,
    getId: (p: Proforma) => p.id,
    getLabel: (p: Proforma) => p.number,
  },
  {
    key: "invoices",
    label: "Factures",
    list: invoiceApi.trash,
    restore: invoiceApi.restore,
    getId: (i: Invoice) => i.id,
    getLabel: (i: Invoice) => i.number ?? `#${i.id}`,
  },
  {
    key: "users",
    label: "Utilisateurs",
    list: usersApi.trash,
    restore: usersApi.restore,
    getId: (u: User) => u.id,
    getLabel: (u: User) => `${u.fullName} (${u.email})`,
  },
  {
    key: "commissions",
    label: "Commissions",
    list: commissionApi.trash,
    restore: commissionApi.restore,
    getId: (c: CommissionTransaction) => c.id,
    getLabel: (c: CommissionTransaction) => `${c.type} — ${c.commissionAmount} XAF`,
  },
  {
    key: "stock",
    label: "Stock",
    list: stockApi.trash,
    restore: stockApi.restore,
    getId: (s: StockArticle) => s.id,
    getLabel: (s: StockArticle) => s.name,
  },
];

function TrashModuleList({ module }: { module: TrashModule<any> }) {
  const queryClient = useQueryClient();
  const [pendingRestore, setPendingRestore] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["trash", module.key],
    queryFn: () => module.list().then((r) => r.data),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: number) => module.restore(id),
    onSuccess: () => {
      toast.success("Élément restauré.");
      queryClient.invalidateQueries({ queryKey: ["trash", module.key] });
    },
    onSettled: () => setPendingRestore(null),
  });

  if (isLoading) return <p className="text-xs text-muted-foreground">Chargement...</p>;
  if (!data || data.length === 0) {
    return <p className="text-xs text-muted-foreground">Corbeille vide.</p>;
  }

  return (
    <>
      <ul className="divide-y divide-border border border-border bg-card">
        {data.map((item) => (
          <li
            key={module.getId(item)}
            className="flex items-center justify-between gap-3 px-4 py-3"
          >
            <span className="text-sm text-foreground">{module.getLabel(item)}</span>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setPendingRestore(module.getId(item))}
            >
              <RotateCcw size={13} /> Restaurer
            </Button>
          </li>
        ))}
      </ul>

      <ConfirmDialog
        open={pendingRestore !== null}
        onOpenChange={(open) => !open && setPendingRestore(null)}
        title="Restaurer cet élément ?"
        description="L'élément redeviendra visible dans les listes normales."
        confirmLabel="Restaurer"
        loading={restoreMutation.isPending}
        onConfirm={() => {
          if (pendingRestore !== null) restoreMutation.mutate(pendingRestore);
        }}
      />
    </>
  );
}

export default function TrashPage() {
  usePageHeader({
    title: "Corbeille",
    guide: {
      steps: [
        "Les éléments supprimés (super admin) restent ici, restaurables à tout moment.",
        "Aucune suppression définitive n'a lieu — la corbeille n'a pas de purge automatique.",
      ],
    },
  });

  return (
    <div className="space-y-5">
      <Tabs defaultValue={modules[0].key}>
        <TabsList>
          {modules.map((m) => (
            <TabsTrigger key={m.key} value={m.key}>
              {m.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {modules.map((m) => (
          <TabsContent key={m.key} value={m.key}>
            <TrashModuleList module={m} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
